import { useState } from "react";
import { motion } from "framer-motion";
import { Globe, Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGUAGES } from "../i18n/translations";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { id: "programs", label: t.nav.programs, testid: "navbar-link-programs" },
    { id: "projects", label: t.nav.projects, testid: "navbar-link-projects" },
    { id: "careers", label: t.nav.careers, testid: "navbar-link-careers" },
    { id: "manifesto", label: t.nav.manifesto, testid: "navbar-link-manifesto" },
    { id: "community", label: t.nav.community, testid: "navbar-link-community" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#FAF7F2]/85 border-b border-[#E7DFD5]/80">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <a href="#top" data-testid="navbar-brand-logo" className="flex items-center gap-2.5 shrink-0" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <span className="w-8 h-8 rounded-full bg-[#C85A17] flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4.5 h-4.5 w-5 h-5" fill="#FAF7F2"><path d="M12 2l2.2 5.6 5.9-1.6-3.1 5.2 4.6 4-5.8.9L14 22l-2-4.3L10 22l-1.8-5.9-5.8-.9 4.6-4L3.9 6l5.9 1.6L12 2z"/></svg>
          </span>
          <span className="font-display font-bold text-[#17181A] tracking-tight text-sm sm:text-base leading-none">
            Maple Future<br className="sm:hidden" /> Institution
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button key={l.id} data-testid={l.testid} onClick={() => scrollTo(l.id)}
              className="text-sm font-medium text-[#5F6368] hover:text-[#C85A17] transition-colors duration-200">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button data-testid="navbar-language-selector" onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-sm font-medium text-[#17181A] border border-[#E7DFD5] rounded-full px-3 py-1.5 hover:border-[#C85A17] transition-colors duration-200 bg-white/60">
              <Globe className="w-4 h-4 text-[#C85A17]" />
              {LANGUAGES.find((l) => l.code === lang)?.short}
            </button>
            {langOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}
                className="absolute right-0 mt-2 w-40 bg-white border border-[#E7DFD5] rounded-xl shadow-lg overflow-hidden z-50">
                {LANGUAGES.map((l) => (
                  <button key={l.code} data-testid={`language-option-${l.code}`}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${lang === l.code ? "bg-[#FFF3EB] text-[#C85A17] font-semibold" : "text-[#17181A] hover:bg-[#FAF7F2]"}`}>
                    {l.label}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
          <button data-testid="navbar-enroll-cta-button" onClick={() => scrollTo("enroll")}
            className="hidden sm:inline-flex items-center bg-[#C85A17] text-[#FDFCF7] text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-[#a84a12] transition-colors duration-200">
            {t.nav.enroll}
          </button>
          <button data-testid="navbar-mobile-menu-button" className="lg:hidden p-2 text-[#17181A]" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#E7DFD5] bg-[#FAF7F2] px-5 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <button key={l.id} data-testid={`mobile-${l.testid}`} onClick={() => { scrollTo(l.id); setMobileOpen(false); }}
              className="text-left text-base font-medium text-[#17181A] py-1.5">
              {l.label}
            </button>
          ))}
          <button data-testid="mobile-enroll-cta-button" onClick={() => { scrollTo("enroll"); setMobileOpen(false); }}
            className="mt-2 bg-[#C85A17] text-[#FDFCF7] text-sm font-semibold rounded-full px-5 py-3 text-center">
            {t.nav.enroll}
          </button>
        </div>
      )}
    </header>
  );
};
